import { useEffect, useMemo, useState } from 'react'
import { Link, useOutletContext, useParams } from 'react-router-dom'
import { ScrollText, Info, ExternalLink } from 'lucide-react'
import { apiGet, cachedGet } from '../../lib/api.js'
import { asList } from '../../lib/chart-helpers.js'
import { usePatientLive } from '../../lib/patientLive.js'
import {
  buildEvents, groupByDay, applyFilter, lineAmount,
  EV, EV_LABEL, FILTERS,
} from '../../lib/billingAudit.js'
import {
  PageHeader, Segmented, Badge, Loading, EmptyState, NoMatchState, ErrorState,
  fmtDate, fmtTime, fmtMoney,
} from '../../kit'

// Rose for a void, amber for an edit, slate for a create — the same reading as
// billingAudit's EV_TONE, on the kit's Badge. A deletion is the one a reviewer
// must never miss, so it is the only alarming colour.
const EV_BADGE = {
  [EV.CREATED]: 'slate',
  [EV.EDITED]: 'amber',
  [EV.VOIDED]: 'rose',
  [EV.OTHER]: 'slate',
}

// ── /patient/:id/log — ประวัติการแก้ไข Billing ───────────────────────────────
//
// The read side of an audit trail that has been WRITTEN for years and never
// shown. `billing.log[]` was unreachable from the app: api 179's output
// whitelist omits it and all seven of its cases filter `active == true`, so a
// voided line — the thing an audit trail exists for — was invisible everywhere.
// GET /api/billing/audit → api 571 → fn 74 reads it, voids included.
//
// WHY THIS PAGE MATTERS MORE THAN THE DAY LOCK. The lock
// (functions/api/billing/_guard.js) freezes closed days, but the fraud it cannot
// see is same-day: money taken at 15:00 and booked lower an hour later, inside
// the open window. Nothing prevents that. What deters it is being named — so
// this page is deliberately visible to EVERY employee, not just managers. The
// deterrent only works if people know the record is readable. Tighten it to
// canManage(me) if that turns out to be the wrong call; nothing else depends on
// the audience.
//
// ⚠️ WHAT THIS PAGE CANNOT TELL YOU, and says so on screen:
//   • Bubble's POST /billing is a full-record REPLACE that wipes log[], so while
//     Bubble is still production most edits leave no trace at all. Its deletes
//     and promotion adds DO log. Coverage improves as the CRM takes the writes.
//   • On rows migrated from Bubble, `Creation_Date` is the IMPORT date
//     (Dec 2022), not when the line was really written — visible as a "created"
//     event years after the visit it belongs to.
// Both are stated in the footnote rather than hidden, because an audit trail
// that quietly overstates its own completeness is worse than none.

export default function PatientLog() {
  const { id } = useParams()
  const { live } = useOutletContext() || {}
  const [rows, setRows] = useState(null)
  // Bumped by a realtime event → refetches the audit trail in place.
  const [tick, setTick] = useState(0)
  const [error, setError] = useState(null)
  const [filter, setFilter] = useState('all')
  const [typeById, setTypeById] = useState(new Map())
  const [empById, setEmpById] = useState(new Map())

  // Live: every billing write (and the treatment edits that move its lines) adds
  // a row to the trail this page reads.
  usePatientLive(live, ['billing', 'treatment'], () => setTick((t) => t + 1))

  // Clearing is keyed on the patient alone: a live refetch must not drop the page
  // back to its spinner while someone is reading the trail.
  useEffect(() => { setRows(null); setError(null) }, [id])

  useEffect(() => {
    let alive = true
    apiGet('/api/billing/audit', { patient_id: id })
      .then((r) => { if (alive) { setRows(asList(r)); setError(null) } })
      .catch((e) => { if (alive) { setError(e?.message || 'โหลดประวัติไม่สำเร็จ'); setRows([]) } })
    return () => { alive = false }
  }, [id, tick])

  // The item name. fn 74 returns billing_type_id raw — its addons came back
  // empty against an explicit output whitelist, and one cached catalogue read
  // beats an addon lookup per row anyway.
  useEffect(() => {
    cachedGet('/api/billing-types')
      .then((r) => setTypeById(new Map(asList(r).map((t) => [String(t.id), t]))))
      .catch(() => {})
  }, [])

  // Names for INFERRED events only — every logged entry already embeds the
  // actor's name in its message. /api/hr/employees is manager-gated, so this is
  // best-effort by design: a 403 leaves the map empty and the id renders as
  // `#40`, which is still a usable pointer. Never surfaced as an error.
  useEffect(() => {
    cachedGet('/api/hr/employees', { per_page: 500 })
      .then((r) => setEmpById(new Map(asList(r).map((e) => [String(e.id), e]))))
      .catch(() => {})
  }, [])

  const events = useMemo(() => buildEvents(rows || []), [rows])
  const shown = useMemo(() => applyFilter(events, filter), [events, filter])
  const groups = useMemo(() => groupByDay(shown), [shown])

  const counts = useMemo(() => {
    const c = { all: events.length }
    for (const e of events) c[e.type] = (c[e.type] || 0) + 1
    return c
  }, [events])

  const typeName = (row) => {
    const t = typeById.get(String(row?.billing_type_id))
    return t?.name || t?.nameEng || row?.detail || `รายการ #${row?.billing_type_id ?? '—'}`
  }
  const actorName = (e) => {
    if (!e.employee_id) return ''
    const emp = empById.get(String(e.employee_id))
    return emp?.code || emp?.name || `#${e.employee_id}`
  }

  if (rows === null) return <Loading label="กำลังโหลดประวัติ…" />

  // A failed load is shown INSTEAD of the list, never next to an empty message.
  const retry = () => { setError(null); setRows(null); setTick((t) => t + 1) }

  return (
    <div className="pb-10">
      {/* The menu says "Log" — so does the title (CONVENTIONS § page layout). */}
      <PageHeader level={2} title="Log"
        subtitle="ประวัติการแก้ไข Billing — ใครเพิ่ม แก้ไข หรือลบรายการเรียกเก็บของคนไข้รายนี้ รวมรายการที่ถูกลบไปแล้ว" />

      {error ? (
        <ErrorState title="โหลดประวัติไม่สำเร็จ" message={error} onRetry={retry} />
      ) : (
        <>
          {/* Filters carry their own counts — "ลบรายการ 3" answers the question a
              reviewer actually opens this page with, before they click anything. */}
          <Segmented className="mb-4" value={filter} onChange={setFilter}
            options={FILTERS.map((f) => ({ value: f.key, label: f.label, count: counts[f.key] || 0 }))} />

          {!shown.length ? (
            events.length
              ? <NoMatchState variant="card" onClear={() => setFilter('all')} />
              : <EmptyState icon={ScrollText} title="ยังไม่มีประวัติการแก้ไขของคนไข้รายนี้" />
          ) : (
            <div className="space-y-5">
              {groups.map((g) => (
                <section key={g.day}>
                  <div className="flex items-baseline gap-2 mb-1.5">
                    <h3 className="text-[13px] font-semibold text-slate-700 tabular-nums">{fmtDate(g.day)}</h3>
                    <span className="text-[11.5px] text-slate-400">{g.items.length} รายการ</span>
                  </div>
                  <div className="rounded-2xl border border-slate-200 bg-white divide-y divide-slate-100">
                    {g.items.map((e) => (
                      <article key={e.key} className="flex items-start gap-3 px-4 py-2.5">
                        <div className="flex-none w-[46px] text-[12px] tabular-nums text-slate-400 pt-0.5">
                          {fmtTime(e.ts) || '—'}
                        </div>
                        <div className="flex-none pt-0.5">
                          <Badge tone={EV_BADGE[e.type] || 'slate'}>{EV_LABEL[e.type]}</Badge>
                        </div>
                        <div className="min-w-0 flex-1">
                          <div className="text-[13.5px] text-slate-800 flex items-center gap-1.5 flex-wrap">
                            <span className={e.row.active === false ? 'line-through decoration-rose-300' : ''}>
                              {typeName(e.row)}
                            </span>
                            {e.row.advancePay && <Badge tone="brand">{e.row.advancePay}</Badge>}
                          </div>
                          {/* The recorded sentence, verbatim. It already names the
                              actor and states the figures, in whichever format the
                              writer used — never paraphrased here. */}
                          {e.message ? (
                            <div className="text-[12px] text-slate-500 mt-0.5 break-words">{e.message}</div>
                          ) : (
                            <div className="text-[12px] text-slate-400 mt-0.5 italic">
                              ไม่มีข้อความบันทึก
                              {actorName(e) && <> · โดย {actorName(e)}</>}
                              {' · '}สร้างขึ้นจากข้อมูลของแถว ไม่ใช่บันทึกจริง
                            </div>
                          )}
                        </div>
                        <div className="flex-none text-right">
                          <div className="text-[13.5px] font-semibold tabular-nums text-slate-800">
                            {fmtMoney(lineAmount(e.row))}
                          </div>
                          {/* billing.treatment_id is the treatment's id → open that visit's chart. */}
                          <Link to={e.row.treatment_id ? `/patient/${id}/chart/history/${e.row.treatment_id}` : `/patient/${id}/treatments`}
                            className="inline-flex items-center gap-1 text-[11.5px] text-slate-400 hover:text-brand-700">
                            เคส #{e.row.treatment_id} <ExternalLink size={10} />
                          </Link>
                        </div>
                      </article>
                    ))}
                  </div>
                </section>
              ))}
            </div>
          )}
        </>
      )}

      {/* Say plainly what the trail does not cover. An audit page that implies
          completeness it does not have is worse than no page. */}
      <div className="mt-6 rounded-xl bg-slate-50 border border-slate-100 p-3 text-[12px] text-slate-500 flex items-start gap-2">
        <Info size={13} className="flex-none mt-0.5 text-slate-400" />
        <div className="space-y-1">
          <div>
            <span className="font-medium text-slate-600">ความครบถ้วนของประวัติ</span> — ระบบเดิม (Bubble)
            เขียนทับรายการทั้งแถวเมื่อแก้ไข ทำให้บันทึกการแก้ไขหายไป จึงเห็นเฉพาะ
            <b> การลบ</b> และ <b>การเพิ่มจากโปรโมชั่น</b> เป็นหลัก ส่วนการแก้ไขจะถูกบันทึกครบ
            เมื่อระบบใหม่เป็นผู้เขียนข้อมูลทั้งหมด
          </div>
          <div>
            รายการที่ย้ายมาจากระบบเดิมจะแสดงวันที่สร้างเป็น <b>วันที่นำเข้าข้อมูล</b> (ธ.ค. 2022)
            ไม่ใช่วันที่บันทึกจริง
          </div>
          <div>
            บรรทัดที่เขียนว่า “สร้างขึ้นจากข้อมูลของแถว” คือรายการที่ระบบประกอบขึ้นจาก
            วันที่สร้าง/วันที่แก้ไขล่าสุด เพราะไม่มีข้อความบันทึกไว้
          </div>
        </div>
      </div>
    </div>
  )
}
